/**
 * Theme configuration and Telegram themeParams integration
 * Manages CSS variables based on Telegram themeParams
 */

import { TelegramWebAppUtils } from "../api/telegram";
import { isDevMode } from "./dev-mode";

export interface ThemeColors {
  accent_text_color?: string;
  bg_color?: string;
  bottom_bar_bg_color?: string;
  button_color?: string;
  button_text_color?: string;
  destructive_text_color?: string;
  header_bg_color?: string;
  hint_color?: string;
  link_color?: string;
  secondary_bg_color?: string;
  section_bg_color?: string;
  section_header_text_color?: string;
  section_separator_color?: string;
  subtitle_text_color?: string;
  text_color?: string;
}

// Default theme colors
const defaultLightTheme: ThemeColors = {
  accent_text_color: "#168acd",
  bg_color: "#ffffff",
  bottom_bar_bg_color: "#ffffff",
  button_color: "#40a7e3",
  button_text_color: "#ffffff",
  destructive_text_color: "#d14e4e",
  header_bg_color: "#ffffff",
  hint_color: "#999999",
  link_color: "#168acd",
  secondary_bg_color: "#f1f1f1",
  section_bg_color: "#ffffff",
  section_header_text_color: "#168acd",
  section_separator_color: "#e7e7e7",
  subtitle_text_color: "#999999",
  text_color: "#000000",
};

const defaultDarkTheme: ThemeColors = {
  accent_text_color: "#007aff",
  bg_color: "#282828",
  bottom_bar_bg_color: "#3e464c",
  button_color: "#007aff",
  button_text_color: "#ffffff",
  destructive_text_color: "#ff453a",
  header_bg_color: "#1c1c1c",
  hint_color: "#ffffff",
  link_color: "#007aff",
  secondary_bg_color: "#1c1c1c",
  section_bg_color: "#282828",
  section_header_text_color: "#e5e5e5",
  section_separator_color: "#3d3d3d",
  subtitle_text_color: "#ffffff",
  text_color: "#ffffff",
};

/**
 * Convert hex color to CSS variable format
 */
function normalizeColor(color?: string): string | undefined {
  if (!color) return undefined;
  // Telegram may send colors with or without #
  return color.startsWith("#") ? color : `#${color}`;
}

/**
 * Set CSS variable on document root
 */
function setCSSVariable(name: string, value: string | undefined): void {
  if (typeof document === "undefined" || !value) return;
  
  const cssName = `--${name.replace(/_/g, "-")}`;
  document.documentElement.style.setProperty(cssName, value);
}

/**
 * Get theme colors from Telegram or use defaults
 */
export function getThemeColors(): ThemeColors {
  if (isDevMode() || !TelegramWebAppUtils.isAvailable()) {
    // In dev mode or when Telegram is not available, use defaults based on system preference
    const prefersDark = typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches;
    return prefersDark ? defaultDarkTheme : defaultLightTheme;
  }

  try {
    // Get themeParams from Telegram WebApp
    const themeParams = TelegramWebAppUtils.getThemeParams();
    
    if (themeParams) {
      return {
        accent_text_color: normalizeColor(themeParams.accent_text_color),
        bg_color: normalizeColor(themeParams.bg_color),
        bottom_bar_bg_color: normalizeColor(themeParams.bottom_bar_bg_color),
        button_color: normalizeColor(themeParams.button_color),
        button_text_color: normalizeColor(themeParams.button_text_color),
        destructive_text_color: normalizeColor(themeParams.destructive_text_color),
        header_bg_color: normalizeColor(themeParams.header_bg_color),
        hint_color: normalizeColor(themeParams.hint_color),
        link_color: normalizeColor(themeParams.link_color),
        secondary_bg_color: normalizeColor(themeParams.secondary_bg_color),
        section_bg_color: normalizeColor(themeParams.section_bg_color),
        section_header_text_color: normalizeColor(themeParams.section_header_text_color),
        section_separator_color: normalizeColor(themeParams.section_separator_color),
        subtitle_text_color: normalizeColor(themeParams.subtitle_text_color),
        text_color: normalizeColor(themeParams.text_color),
      };
    }
  } catch (error) {
    console.warn("[Theme] Failed to get Telegram themeParams", error);
  }

  // Fallback to defaults
  const prefersDark = typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches;
  return prefersDark ? defaultDarkTheme : defaultLightTheme;
}

/**
 * Apply theme colors to CSS variables
 */
export function applyThemeColors(colors?: ThemeColors): void {
  const themeColors = colors || getThemeColors();

  // Apply all color variables
  setCSSVariable("accent-text-color", themeColors.accent_text_color);
  setCSSVariable("bg-color", themeColors.bg_color);
  setCSSVariable("bottom-bar-bg-color", themeColors.bottom_bar_bg_color);
  setCSSVariable("button-color", themeColors.button_color);
  setCSSVariable("button-text-color", themeColors.button_text_color);
  setCSSVariable("destructive-text-color", themeColors.destructive_text_color);
  setCSSVariable("header-bg-color", themeColors.header_bg_color);
  setCSSVariable("hint-color", themeColors.hint_color);
  setCSSVariable("link-color", themeColors.link_color);
  setCSSVariable("secondary-bg-color", themeColors.secondary_bg_color);
  setCSSVariable("section-bg-color", themeColors.section_bg_color);
  setCSSVariable("section-header-text-color", themeColors.section_header_text_color);
  setCSSVariable("section-separator-color", themeColors.section_separator_color);
  setCSSVariable("subtitle-text-color", themeColors.subtitle_text_color);
  setCSSVariable("text-color", themeColors.text_color);

  // Set theme attribute for CSS selectors
  if (typeof document !== "undefined") {
    const isDark = themeColors.bg_color && 
                   (themeColors.bg_color.toLowerCase() === "#282828" || 
                    themeColors.bg_color.toLowerCase() === "#1a2c33" ||
                    parseInt(themeColors.bg_color.replace("#", ""), 16) < parseInt("888888", 16));
    document.documentElement.setAttribute("data-theme", isDark ? "dark" : "light");
  }
}

/**
 * Initialize theme on app startup
 */
export function initializeTheme(): void {
  if (typeof window === "undefined") return;

  // Apply theme immediately
  applyThemeColors();

  // Listen for Telegram theme changes
  if (TelegramWebAppUtils.isAvailable()) {
    try {
      TelegramWebAppUtils.events.on("themeChanged", () => {
        console.log("[Theme] Telegram theme changed, updating colors");
        applyThemeColors();
      });
    } catch (error) {
      console.warn("[Theme] Failed to listen for theme changes", error);
    }
  }

  // Also listen for system theme changes (for dev mode)
  if (isDevMode()) {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = () => {
      console.log("[Theme] System theme changed, updating colors");
      applyThemeColors();
    };
    mediaQuery.addEventListener("change", handleChange);
  }
}

